import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Loader } from "@/components/animate-ui/icons/loader";
import { completeGoogleAdsOAuth } from "@/lib/api/integrations";
import { useAuthStore } from "@/stores/auth-store";

export const Route = createFileRoute("/integrations/google-ads/callback")({
	component: GoogleAdsCallbackPage,
});

function GoogleAdsCallbackPage() {
	const navigate = useNavigate();
	const hasRun = useRef(false);
	const isInitialized = useAuthStore((state) => state.isInitialized);
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

	// Mutation to complete OAuth flow
	const completeMutation = useMutation({
		retry: false, // OAuth codes can only be used once, no retries
		mutationFn: async ({ code, state }: { code: string; state: string }) => {
			const callbackUrl = `${window.location.origin}/integrations/google-ads/callback`;
			return completeGoogleAdsOAuth(code, state, callbackUrl);
		},
		onSuccess: () => {
			// Redirect to integrations page with success status
			navigate({
				to: "/integrations",
				search: {
					oauth: "success",
					provider: "google-ads",
					message: undefined,
					composio_connection_id: undefined,
					composio_app_integration_id: undefined,
				},
			});
		},
		onError: (error) => {
			// Redirect to integrations page with error status
			console.error("OAuth completion error:", error);
			const errorMessage = error instanceof Error ? error.message : "Unknown error";
			navigate({
				to: "/integrations",
				search: {
					oauth: "error",
					provider: "google-ads",
					message: errorMessage,
					composio_connection_id: undefined,
					composio_app_integration_id: undefined,
				},
			});
		},
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: Empty deps with auth checks - must run exactly once when auth ready
	useEffect(() => {
		// Ensure this only runs once (prevent double-execution in React Strict Mode)
		if (hasRun.current) return;

		// Wait for auth store to initialize from localStorage
		if (!isInitialized) {
			return;
		}

		// Ensure user is authenticated before proceeding
		if (!isAuthenticated) {
			// User not logged in - redirect to login with return URL
			hasRun.current = true; // Mark as run to prevent loops
			navigate({
				to: "/login",
				search: {
					redirect: `/integrations/google-ads/callback${window.location.search}`,
				},
			});
			return;
		}

		// Auth is ready, mark as run and proceed
		hasRun.current = true;

		// Get code and state from URL
		const params = new URLSearchParams(window.location.search);
		const code = params.get("code");
		const state = params.get("state");
		const errorParam = params.get("error");

		if (errorParam) {
			// OAuth was denied or failed
			navigate({
				to: "/integrations",
				search: {
					oauth: "error",
					provider: "google-ads",
					message: errorParam,
					composio_connection_id: undefined,
					composio_app_integration_id: undefined,
				},
			});
			return;
		}

		if (!code || !state) {
			// Missing required parameters
			navigate({
				to: "/integrations",
				search: {
					oauth: "error",
					provider: "google-ads",
					message: "Missing authorization code or state",
					composio_connection_id: undefined,
					composio_app_integration_id: undefined,
				},
			});
			return;
		}

		// Complete OAuth flow (only once, with authenticated user)
		completeMutation.mutate({ code, state });
	}, [isInitialized, isAuthenticated]);

	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<div className="mx-auto max-w-md space-y-6 text-center">
				<Loader className="mx-auto size-16 animate-spin text-primary" />
				<div className="space-y-2">
					<h1 className="text-2xl font-semibold tracking-tight">Completing Authorization...</h1>
					<p className="text-muted-foreground">Connecting your account.</p>
				</div>
			</div>
		</div>
	);
}
